import { HelpCircle } from 'lucide-react';
import { useTour, type TourDefinition } from '@renderer/hooks/useTour';

interface Props {
  tour: TourDefinition;
  /** Texto del botón. Si se omite, usa `tour.title`. */
  label?: string;
  /**
   * Variante visual.
   *   - `prominent` (default Sprint 9.2): ámbar, ≥40 px de alto, alto contraste.
   *     Pensado para que una cajera con nivel Excel básico lo encuentre rápido.
   *   - `subtle`: gris discreto, para headers densos donde la ayuda es secundaria.
   *     Mantenida como opt-in; no se debe usar por default tras el feedback del Sprint 9.1.
   */
  variant?: 'subtle' | 'prominent';
  className?: string;
}

/**
 * Sprint 9.1 — Botón de ayuda contextual.
 * Sprint 9.2 — Rediseño UX tras feedback del dueño:
 *   "...la letra quedó muy pequeña y el color de Font es gris, no resalta...".
 *   Defaults agresivamente visibles, ≥40 px (Fitts), ámbar cálido, pulse en el badge.
 *
 * El badge "Nuevo" parpadea hasta que el usuario complete el tour una vez.
 * Después del primer uso, el botón se queda con el mismo styling pero sin pulse
 * — sigue disponible para que la cajera lo repita cuando quiera.
 */
export function HelpButton({ tour, label, variant = 'prominent', className = '' }: Props) {
  const { runTour, hasSeenTour, loading } = useTour(tour);
  const texto = label ?? `¿Cómo funciona? · ${tour.title}`;

  const baseStyle =
    variant === 'prominent'
      ? 'inline-flex items-center gap-2 rounded-xl border-2 border-amber-400 bg-amber-100 px-4 py-2.5 text-sm font-semibold text-amber-900 shadow-sm hover:bg-amber-200 hover:border-amber-500 transition min-h-[44px]'
      : 'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 transition';

  // Cuando aún no vio el tour y la variante es prominente, animamos sutilmente
  // el botón entero para que la mirada periférica lo encuentre solo.
  const pulseHint = variant === 'prominent' && !loading && !hasSeenTour ? 'animate-pulse' : '';

  return (
    <button
      type="button"
      onClick={runTour}
      className={`relative ${baseStyle} ${pulseHint} ${className}`}
      title={`Ver guía: ${texto}`}
    >
      <HelpCircle className={variant === 'prominent' ? 'h-5 w-5' : 'h-3.5 w-3.5'} />
      <span>{texto}</span>
      {/*
        Sprint 152 — El distintivo va DENTRO de la fila, no flotando sobre la esquina.

        Con `absolute -top-2 -right-2` se montaba encima del propio botón y **tapaba su texto**:
        en producción se leía "¿Cómo funciona? · Cómo leer los repo…". Un aviso que esconde
        aquello de lo que avisa no informa, estorba. Y a 10 px tampoco se leía.
      */}
      {!loading && !hasSeenTour && (
        <span className="ml-1 shrink-0 rounded-full bg-rose-600 px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-white">
          Nuevo
        </span>
      )}
    </button>
  );
}
